import { styled } from '@mui/material/styles';
import { useContext } from 'react';
import Box from '@mui/material/Box';
import ErrorIcon from '@mui/icons-material/Error';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { numberFormat } from 'helpers/i18n';
import Logo03 from 'assets/overviewSection/PS-Logo03.svg';

const TooltipBlue = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: '#182D55'
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#182D55',
        border: '1px solid #182D55',
        borderRadius: 8,
        boxShadow: '0px 2px 10px rgba(24, 45, 85, 0.1)',
        minWidth: 293,
        padding: '16px 24px'
    }
}));

const CreditModelV1Overview = () => {
    const { statistics } = useContext(CardContext);
    const { hasAccount } = useContext(AuthContext);

    const getCreditLimit = () => {
        if (!hasAccount) {
            return numberFormat(0, 2, 2);
        }
        return numberFormat(statistics.creditLimit, 2, 2);
    };

    return (
        <Box
            display="flex"
            sx={{
                flexDirection: {
                    md: 'row',
                    xs: 'column'
                }
            }}>
            <Box
                className="statistic-card background-color-main"
                sx={{
                    backgroundImage: `url(${Logo03})`,
                    backgroundRepeat: 'no-repeat'
                }}>
                <Box className="statistic-card-info">
                    <Typography className="statistic-title white-text">Credit Line Limit</Typography>
                    <Typography className="statistic-value white-text">{getCreditLimit()}</Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-main" sx={{ padding: '10px' }}>
                <Box id="statistic-card-inner-box">
                    <Box className="statistic-card-info" sx={{ padding: '10px 0px' }}>
                        <Typography className="statistic-title white-text">Total Allocated/Unallocated</Typography>
                        <Typography className="statistic-value white-text">
                            {numberFormat(statistics.allocated, 2, 2)}/
                            <Box component="span" sx={{ fontSize: '16px' }}>
                                {numberFormat(statistics.creditLimit - statistics.allocated, 2, 2)}
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white" sx={{ borderBottom: '3px solid #FF8897' }}>
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">Total spent today</Typography>
                    <Typography className="statistic-value black-text">{numberFormat(statistics.businessSpent, 2, 2)}</Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white" sx={{ borderBottom: '3px solid #98CD6C' }}>
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">
                        Available to spend
                        <TooltipBlue
                            placement="top"
                            arrow
                            title={
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '5px',
                                            fontSize: '13px'
                                        }}>
                                        <Typography className="tooltip-stat-text white-text font-weight-bold">
                                            Available to spend:
                                        </Typography>
                                        <Typography className="tooltip-stat-text white-text font-weight-bold">
                                            {numberFormat(statistics.creditLimit - statistics.businessSpent, 2, 2)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '5px'
                                        }}>
                                        <Typography className="tooltip-stat-text white-text">Credit Line Limit:</Typography>
                                        <Typography className="tooltip-stat-text white-text">{getCreditLimit()}</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '5px'
                                        }}>
                                        <Typography className="tooltip-stat-text white-text">
                                            Total amount spent today:
                                        </Typography>
                                        <Typography className="tooltip-stat-text white-text">
                                            -${statistics.businessSpent}
                                        </Typography>
                                    </Box>
                                </>
                            }>
                            <ErrorIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                        </TooltipBlue>
                    </Typography>
                    <Typography className="statistic-value black-text">
                        {numberFormat(statistics.creditLimit - statistics.businessSpent, 2, 2)}
                    </Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white">
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">Active cards</Typography>
                    <Typography className="statistic-value black-text">{statistics.activeCards}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default CreditModelV1Overview;
